import React, { useMemo, useState, useEffect } from 'react';
import { ColDef } from 'ag-grid-community';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useTheme } from '@/app/contexts/ThemeContext';
import Header from '@/shared/components/layout/Header';
import SEOHead from '@/shared/components/common/SEOHead';
import DataTable from '@/shared/components/common/DataTable';
import { Order } from '../types/pricing.types';

const OrdersPage: React.FC = () => {
  const { t, isRTL, language } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Update document direction
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language === 'fa' ? 'fa' : 'en';
  }, [isRTL, language]);

  // Mock data - در واقعیت از API می‌آید
  const [orders] = useState<Order[]>([
    {
      id: 'ORD-001',
      plan: 'monthly',
      price: 163500,
      tax: 13500,
      status: 'paid',
      date: '2025-01-15',
      expiryDate: '2025-02-15',
      paymentMethod: 'online',
      invoiceUrl: 'https://example.com/invoice/ORD-001',
    },
    {
      id: 'ORD-002',
      plan: 'yearly',
      price: 990000,
      tax: 90000,
      status: 'pending',
      date: '2025-01-20',
      expiryDate: '2026-01-20',
      paymentMethod: 'sheba',
    },
    {
      id: 'ORD-003',
      plan: 'monthly',
      price: 163500,
      tax: 13500,
      status: 'expired',
      date: '2024-12-10',
      expiryDate: '2025-01-10',
      invoiceUrl: 'https://example.com/invoice/ORD-003',
    },
    {
      id: 'ORD-004',
      plan: 'yearly',
      price: 990000,
      tax: 90000,
      status: 'cancelled',
      date: '2024-11-05',
      expiryDate: '2025-11-05',
    },
  ]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fa-IR').format(price);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fa-IR');
  };

  const getStatusBadge = (status: Order['status']) => {
    const statusMap = {
      pending: { text: t('orders.pending'), color: 'bg-yellow-500' },
      paid: { text: t('orders.paid'), color: 'bg-green-500' },
      expired: { text: t('orders.expired'), color: 'bg-red-500' },
      cancelled: { text: t('orders.cancelled'), color: 'bg-gray-500' },
    };
    return statusMap[status];
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('orders.orderId'),
        width: 150,
        pinned: isRTL ? 'right' : 'left',
      },
      {
        field: 'plan',
        headerName: t('orders.plan'),
        width: 100,
        valueFormatter: (params) => {
          const planMap: Record<string, string> = {
            free: t('pricing.free'),
            monthly: t('pricing.monthly'),
            yearly: t('pricing.yearly'),
          };
          return planMap[params.value] || params.value;
        },
      },
      {
        field: 'price',
        headerName: t('orders.price'),
        width: 150,
        valueFormatter: (params) => `${formatPrice(params.value)} ${t('pricing.toman')}`,
      },
      {
        field: 'tax',
        headerName: t('orders.tax'),
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return '-';
          return `${formatPrice(params.value)} ${t('pricing.toman')}`;
        },
      },
      {
        field: 'status',
        headerName: t('orders.status'),
        width: 130,
        cellRenderer: (params: any) => {
          const status = getStatusBadge(params.value);
          return (
            <span
              className={`w-full px-2 py-1 rounded-full text-white text-xs font-semibold ${status.color} inline-block text-center`}
            >
              {status.text}
            </span>
          );
        },
      },
      {
        field: 'date',
        headerName: t('orders.date'),
        width: 120,
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        field: 'expiryDate',
        headerName: t('orders.expiryDate'),
        width: 120,
        valueFormatter: (params) => {
          if (!params.value) return '-';
          return formatDate(params.value);
        },
      },
      {
        field: 'paymentMethod',
        headerName: 'روش پرداخت',
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return '-';
          return params.value === 'online'
            ? t('pricing.orderSummary.onlinePayment')
            : t('pricing.orderSummary.shebaPayment');
        },
      },
      {
        field: 'invoiceUrl',
        headerName: t('orders.invoice'),
        width: 150,
        cellRenderer: (params: any) => {
          if (!params.value) return '-';
          return (
            <button
              onClick={() => window.open(params.value, '_blank')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                isDark
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              {t('orders.invoice')}
            </button>
          );
        },
      },
    ],
    [t, formatPrice, formatDate, isRTL, isDark]
  );

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    []
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <SEOHead />
      <Header />
      <div className="py-12 px-4">
        <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('orders.title')}
          </h1>
        </div>

        {/* Data Table */}
        <div className="w-full">
          <DataTable
            rowData={orders}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            height="auto"
            gridOptions={{
              domLayout: 'autoHeight',
            }}
          />
        </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;

